import * as UP from "./urlParsers";
import wrappedAxios from "../axiosWrapper";
import {
  FunctionTemplatesAPI,
  TemplateMetadataAPI,
  TemplateEditFormDataAPI,
  TemplateFormDataAPI,
  TemplateCopyFormDataAPI,
} from "./types/templateMetadata";
import { SOR, TemplateId } from "../../globalTypes";
import { EditRowWithColumns } from "../tableRowActions/types";
import { RowKey, Table, TableCells } from "../table/types";
import { ImportTemplates, TemplateModel } from "./types";
import {
  ExportTemplateResponse,
  PostTemplateImports,
  TemplateImportDetail,
  TemplateImports,
  TemplatesToImport,
  UpdateTemplateImportDetail,
} from "./types/templateImport";

export const fetchAllTemplatesMetadata = () =>
  wrappedAxios<TemplateMetadataAPI[]>({
    url: UP.fetchAllTemplatesMetadata(),
    params: {
      navigationOnly: false,
      visibleOnly: false,
    },
    method: "get",
  });

export const fetchVisibleOverviewTemplatesMetadata = () =>
  wrappedAxios<TemplateMetadataAPI[]>({
    url: UP.fetchAllTemplatesMetadata(),
    params: {
      navigationOnly: true,
      visibleOnly: true,
    },
    method: "get",
  });

export const fetchTemplateMetadata = (
  templateId: TemplateId,
  rowKey?: RowKey
) =>
  wrappedAxios<TemplateMetadataAPI>({
    url: UP.fetchTemplateMetadata(templateId, rowKey),
    method: "get",
  });

export const fetchTemplateMetadataBulk = (templateIds: (number | string)[]) =>
  wrappedAxios<TemplateMetadataAPI[]>({
    url: UP.fetchTemplateMetadataBulk(templateIds),
    method: "get",
  });

export const fetchFunctionTemplatesForTemplateId = (templateId: TemplateId) =>
  wrappedAxios<FunctionTemplatesAPI>({
    url: UP.fetchFunctionTemplatesForTemplateId(templateId),
    method: "get",
  });

export const requestCopyTemplate = (data: TemplateCopyFormDataAPI) =>
  wrappedAxios<void>({
    url: UP.copyTemplate(),
    method: "POST",
    data: { ...data },
  });

export const fetchTemplateTableRow = (templateId: TemplateId) =>
  wrappedAxios<EditRowWithColumns>({
    url: UP.getTemplateTableRow(templateId),
    method: "get",
  });

export const requestDeleteTemplate = (templateId: TemplateId) =>
  wrappedAxios<string>({
    url: UP.deleteTemplate(templateId),
    method: "delete",
  });

// Please use administration/fetchAdminTableData
export const fetchAdministrationTemplateTable = (
  pageNum: number,
  maxPageRowCount: number,
  searchOrder: SOR,
  hiddenColumns: string[],
  fulltextKeyword: string | undefined
) =>
  wrappedAxios<Table>({
    url: UP.getAdministrationTemplateTable(
      pageNum,
      maxPageRowCount,
      JSON.stringify(searchOrder),
      hiddenColumns,
      fulltextKeyword
    ),
    method: "GET",
  });

export const insertTemplateTableRow = ({
  system = "db",
  ...rest
}: TemplateFormDataAPI) =>
  wrappedAxios<TemplateModel>({
    url: UP.postTemplate(),
    method: "POST",
    data: { ...rest, system },
  });

export const updateTemplateTableRow = (
  templateData: TemplateEditFormDataAPI,
  rowId: string | number
) =>
  wrappedAxios<string>({
    url: UP.updateTemplate(rowId),
    method: "PATCH",
    data: templateData,
  });

export const getRowsPerPageForTemplateId = (templateId: TemplateId) =>
  wrappedAxios<number>({
    url: UP.getRowsPerPageForTemplateId(templateId),
    method: "get",
  });

export const setRowsPerPageForTemplateId = (
  templateId: TemplateId,
  rowsPerPage: number | string
) =>
  wrappedAxios({
    url: UP.setRowsPerPageForTemplateId(),
    data: {
      templateId,
      rowPageCount: rowsPerPage,
    },
    method: "post",
  });

export const importTemplate = (data: ImportTemplates) =>
  wrappedAxios<string>({
    url: UP.importHegTemplate(),
    method: "POST",
    data,
  });

type GetVersionTableParams = {
  templateId: TemplateId;
  rowsPerPage?: number;
  pageNumber?: number;
  sor?: SOR;
  fulltextQuery?: string;
};

export const fetchTemplateVersionTable = ({
  fulltextQuery,
  pageNumber,
  rowsPerPage,
  templateId,
  sor,
}: GetVersionTableParams) =>
  wrappedAxios<Table>({
    method: "post",
    url: UP.fetchTemplateVersionTable(),
    params: {
      templateId,
      pageNum: pageNumber,
      maxPageRowCount: rowsPerPage,
      fulltextKeyword: fulltextQuery,
    },
    data: sor,
  });

export const getTemplateVersionTableRow = (
  rowKey: RowKey,
  templateId: TemplateId,
  editable: boolean
) =>
  wrappedAxios<EditRowWithColumns>({
    method: "get",
    params: { templateId, editable },
    url: UP.getTemplateVersionTableRow(rowKey),
  });

export const updateTemplateVersionTableRow = (
  rowKey: RowKey,
  cells: TableCells,
  templateId: TemplateId
) =>
  wrappedAxios({
    method: "put",
    params: { templateId },
    url: UP.updateTemplateVersionTableRow(rowKey),
    data: cells,
  });

const getTemplatesSelect = (
  datasourceId: number,
  type: "overview" | "detail" | "item"
) =>
  wrappedAxios<Record<number, string>>({
    url: UP.templatesSelect(),
    params: {
      datasourceId,
      type,
    },
  });

export const getOverviewTemplatesSelect = (datasourceId: number) =>
  getTemplatesSelect(datasourceId, "overview");

// ------------- TEMPLATE IMPORT/EXPORT ---------------

export const getTemlateExports = (templateId: number, exportAll: boolean) =>
  wrappedAxios<ExportTemplateResponse>({
    method: "GET",
    url: UP.templateExportWithId(templateId),
    headers: {
      "Content-Type": "application/octet-stream; charset=utf-8",
    },
    params: { exportAll },
  });

export const getTemplateImports = () =>
  wrappedAxios<TemplateImports>({
    method: "GET",
    url: UP.templateImports(),
  });

export const postTemplateImports = (data: FormData) =>
  wrappedAxios<TemplatesToImport>({
    method: "POST",
    url: UP.templateImports(),
    headers: {
      "Content-Type": "multipart/form-data",
    },
    data,
  });

export const getTemplatesToImportForId = (templateId: number) =>
  wrappedAxios<TemplatesToImport>({
    method: "GET",
    url: UP.templateImportsWithId(templateId),
  });

export const deleteTemplatesToImportForId = (templateId: number) =>
  wrappedAxios<boolean>({
    method: "DELETE",
    url: UP.templateImportsWithId(templateId),
  });

export const getTemplateImportDetail = (detailId: number) =>
  wrappedAxios<TemplateImportDetail>({
    method: "GET",
    url: UP.templateImportDetailsWithId(detailId),
  });

export const patchTemplateImportDetails = (data: UpdateTemplateImportDetail) =>
  wrappedAxios<TemplateImportDetail>({
    method: "PATCH",
    url: UP.templateImportDetails(),
    data: { ...data },
  });

export const postRunTemplateImports = (
  templateImportId: number,
  toggledDetailIds: number[]
) =>
  wrappedAxios<boolean>({
    method: "POST",
    url: UP.runTemplateImports(),
    params: { templateImportId },
    data: [...toggledDetailIds],
  });
